type WikiData = {
  extract?: string
  thumbnail?: { source?: string }
  content_urls?: { desktop?: { page?: string } }
}

type Specs = { [k: string]: string }

type NewsItem = {
  title: string
  link: string
  pubDate: string
}

export type ObjectEnrichment = {
  wiki: WikiData | null
  specs: Specs | null
  src: string | null
  news: NewsItem[]
}

type EnrichmentItem = {
  name: string
  src?: string
  category: string
}

type RssItem = {
  title: string
  link: string
  pubDate: string
}

const fetchWithTimeout = async (url: string, options: any = {}, timeout = 6000) => {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)
  try {
    const response = await fetch(url, { ...options, signal: controller.signal })
    clearTimeout(id)
    return response
  } catch (e) {
    clearTimeout(id)
    throw e
  }
}

const getSearchPrefix = (catName: string) => {
  const lower = catName.toLowerCase()
  if (lower.includes('спг') || lower.includes('lng')) return 'LNG plant'
  if (lower.includes('электро') || lower.includes('power')) return 'power plant'
  if (lower.includes('нпз') || lower.includes('refinery')) return 'oil refinery'
  if (lower.includes('нефт') || lower.includes('oil')) return 'oil terminal'
  return catName
}

const parseXmlRss = (text: string): RssItem[] => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(text, 'text/xml')
    if (doc.querySelector('parsererror')) return []
    const items = doc.querySelectorAll('item')
    const parsed: RssItem[] = []
    items.forEach(item => {
      let link = item.querySelector('link')?.textContent || ''
      if (!link) {
        const linkEl = item.querySelector('link')
        link = linkEl?.nextSibling?.nodeValue || ''
      }
      parsed.push({
        title: item.querySelector('title')?.textContent || 'Без заголовка',
        link,
        pubDate: item.querySelector('pubDate')?.textContent || new Date().toISOString()
      })
    })
    return parsed
  } catch (e) { return [] }
}

const fetchRssFeed = async (feedUrl: string): Promise<RssItem[]> => {
  const proxies = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(feedUrl)}`,
    `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(feedUrl)}`
  ]

  for (const proxyUrl of proxies) {
    try {
      const res = await fetchWithTimeout(proxyUrl, {}, 4000)
      if (res.ok) {
        const text = await res.text()
        const items = parseXmlRss(text)
        if (items.length > 0) return items
      }
    } catch (e) { /* next proxy */ }
  }
  return []
}

const translateText = async (text: string) => {
  if (!text) return ''
  try {
    const chunk = text.substring(0, 500)
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(chunk)}&langpair=en|ru`
    const res = await fetchWithTimeout(url).then(r => r.json())
    if (res.responseData && res.responseData.translatedText && !res.responseData.translatedText.includes('MYMEMORY WARNING')) {
      return res.responseData.translatedText
    }
    return text
  } catch (e) { return text }
}

const fetchSrcContent = async (url: string): Promise<string | null> => {
  if (!url || !url.startsWith('http')) return null
  const proxies = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`
  ]

  for (const proxyUrl of proxies) {
    try {
      const res = await fetchWithTimeout(proxyUrl, {}, 6000)
      if (res.ok) {
        const html = await res.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        const meta = doc.querySelector('meta[name="description"]')?.getAttribute('content') || doc.querySelector('meta[property="og:description"]')?.getAttribute('content')
        if (meta && meta.length > 50) return `${meta.substring(0, 2000)}...`

        const paragraphs = Array.from(doc.querySelectorAll('p, .mw-parser-output > p')).map(p => p.textContent || '').filter(t => t.length > 50)
        if (paragraphs.length > 0) return `${paragraphs.slice(0, 5).join('\n\n').substring(0, 2000)}...`
      }
    } catch (e) { /* next proxy */ }
  }
  return null
}

export const fetchObjectInfoDirect = async (item: EnrichmentItem): Promise<ObjectEnrichment> => {
  const name = item.name
  const srcUrl = item.src || ''
  const searchPrefix = getSearchPrefix(item.category)

  const wikiPromise = (async () => {
    try {
      const searchQuery = `${searchPrefix} ${name}`.trim()
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchQuery)}&format=json&origin=*`
      const searchRes: any = await fetchWithTimeout(searchUrl).then(r => r.json())

      const typeKeywords = ['plant', 'power', 'terminal', 'station', 'refinery', 'dam', 'electric', 'energy', 'lng', 'nuclear', 'solar', 'wind', 'gas', 'hydro', 'storage']

      for (let i = 0; i < Math.min(searchRes.query.search.length, 3); i++) {
        const title = searchRes.query.search[i].title
        const summaryUrl = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`
        const wikiData: any = await fetchWithTimeout(summaryUrl).then(r => r.json())

        const extractLower = (wikiData.extract || '').toLowerCase()
        const isRelevant = typeKeywords.some(kw => extractLower.includes(kw)) || title.toLowerCase().includes(name.toLowerCase())

        if (isRelevant) {
          if (wikiData.extract && !/[а-я]/i.test(wikiData.extract)) {
            wikiData.extract = await translateText(wikiData.extract)
          }

          const parseUrl = `https://en.wikipedia.org/w/api.php?action=parse&page=${encodeURIComponent(title)}&prop=text&section=0&format=json&origin=*`
          const parseRes: any = await fetchWithTimeout(parseUrl).then(r => r.json())
          const html = parseRes.parse.text['*']
          const doc = new DOMParser().parseFromString(html, 'text/html')
          const infobox = doc.querySelector('table.infobox')
          const specs: Specs = {}
          if (infobox) {
            infobox.querySelectorAll('tr').forEach(row => {
              const th = row.querySelector('th')
              const td = row.querySelector('td')
              if (th && td) {
                const key = (th.textContent || '').trim()
                const val = (td.textContent || '').trim().replace(/\[.*?\]/g, '')
                if (key.match(/operat|company|owner/i)) specs['Оператор'] = val
                if (key.match(/capacity|power output|nameplate/i)) specs['Мощность'] = val
                if (key.match(/fuel|source|type|technology/i)) specs['Тип/Топливо'] = val
              }
            })
          }
          return { wiki: wikiData, specs: Object.keys(specs).length > 0 ? specs : null }
        }
      }
      return { wiki: null, specs: null }
    } catch (e) { return { wiki: null, specs: null } }
  })()

  const srcPromise = fetchSrcContent(srcUrl)

  const newsPromise = (async () => {
    if (!name) return []
    const newsQueryStr = `"${name}"`.trim()
    const feeds = [
      `https://news.google.com/rss/search?q=${encodeURIComponent(newsQueryStr)}&hl=en&gl=US&ceid=US:en`,
      `https://news.google.com/rss/search?q=${encodeURIComponent(newsQueryStr)}&hl=ru&gl=RU&ceid=RU:ru`,
      `https://www.bing.com/news/search?q=${encodeURIComponent(newsQueryStr)}&format=rss`
    ]

    const feedResults = await Promise.all(feeds.map(feed => fetchRssFeed(feed)))
    let allItems: RssItem[] = []
    feedResults.forEach(items => { if (items) allItems = allItems.concat(items) })

    const nameLower = name.toLowerCase()
    const seenLinks = new Set<string>()
    const filteredItems = allItems.filter(item => {
      const titleLower = item.title.toLowerCase()
      const isRelevant = titleLower.includes(nameLower)
      if (isRelevant && !seenLinks.has(item.link)) {
        seenLinks.add(item.link)
        return true
      }
      return false
    })

    filteredItems.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime())
    return filteredItems.slice(0, 10).map(item => ({ title: item.title, link: item.link, pubDate: new Date(item.pubDate).toLocaleDateString('ru-RU') }))
  })()

  try {
    const [wikiRes, srcRes, newsRes] = await Promise.allSettled([wikiPromise, srcPromise, newsPromise])
    return {
      wiki: wikiRes.status === 'fulfilled' ? wikiRes.value.wiki : null,
      specs: wikiRes.status === 'fulfilled' ? wikiRes.value.specs : null,
      src: srcRes.status === 'fulfilled' ? srcRes.value : null,
      news: newsRes.status === 'fulfilled' ? newsRes.value : []
    }
  } catch (e) { return { wiki: null, specs: null, src: null, news: [] } }
}
